import React, { memo } from 'react';

import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';

import { StatusType } from '../../types/StatusType';
import { Theme } from '../../types/Theme';
import { Box } from '../Box/Box';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';

interface Props {
  /** The number to display */
  number: number;
  /** Status determines the color of the circle */
  status?: StatusType | 'cta';
  /** If 'dark' theme and no status, will inverse colors. If status is provided, will override theme. */
  theme?: Theme;
  /**
   * `default` — original Badge footprint (icon.l, ~36×36).
   * `compact` — former Count footprint (22×22), e.g. beside Button labels or in Stepper.
   */
  size?: 'default' | 'compact';
}

export const Badge = memo(
  ({ number, status, theme = 'light', size = 'default' }: Props) => {
    const formatNumber = useFormatNumber();
    const ninetyNine = formatNumber(99);
    const isOverLimit = number > 99;
    const displayCount = isOverLimit ? ninetyNine + '+' : formatNumber(number);
    const tooltip = isOverLimit ? formatNumber(number) : undefined;

    let bg: string = 'status.info';
    if (status === 'cta') {
      bg = '#4F73FF';
    } else if (status) {
      bg = `status.${status}`;
    } else if (theme === 'dark') {
      bg = 'background';
    }

    const isCompact = size === 'compact';

    return (
      <Tooltip tooltip={tooltip}>
        <Box
          role="status"
          align="center"
          bg={bg}
          borderRadius={isCompact ? '50%' : 'round'}
          display="flex"
          justify="center"
          h={isCompact ? 22 : 'icon.l'}
          w={isCompact ? 22 : 'icon.l'}
        >
          <Text
            color={
              theme === 'dark' && !status
                ? 'text.primary'
                : 'inverse.text.primary'
            }
            variant={isCompact ? 'tiny-bold' : 'body-bold'}
          >
            {displayCount}
          </Text>
        </Box>
      </Tooltip>
    );
  },
);
